"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PenpalContextProvider = exports.PenpalContext = exports.ConfigMode = void 0;
const react_1 = __importStar(require("react"));
const penpal_1 = require("penpal");
const resize_observer_polyfill_1 = __importDefault(require("resize-observer-polyfill"));
var ConfigMode;
(function (ConfigMode) {
    ConfigMode[ConfigMode["Unknown"] = 0] = "Unknown";
    ConfigMode[ConfigMode["IntegrationsPage"] = 1] = "IntegrationsPage";
    ConfigMode[ConfigMode["FormConfigSubmitActions"] = 2] = "FormConfigSubmitActions";
    ConfigMode[ConfigMode["FormConfigInitialDataActions"] = 3] = "FormConfigInitialDataActions";
})(ConfigMode = exports.ConfigMode || (exports.ConfigMode = {}));
exports.PenpalContext = react_1.default.createContext({
    connected: false,
});
function PenpalContextProvider(props) {
    const resizeObserverRef = react_1.useRef(null);
    const penpalConnectionRef = react_1.useRef(null);
    const [state, setState] = react_1.useState({
        //@ts-ignore - allows storybook load the component
        connected: process.env.NODE_ENV === "test" ? true : false,
    });
    const [triedToConnect, setTriedToConnect] = react_1.useState(false);
    const shouldCancelDisableCallbackRef = react_1.useRef(() => {
        return Promise.resolve(false);
    });
    const setShouldCancelDisableCallback = react_1.useCallback((fn) => {
        shouldCancelDisableCallbackRef.current = fn;
    }, []);
    const assertConnected = react_1.useCallback(() => {
        if (!state.connected || !penpalConnectionRef.current) {
            throw new Error("No Penpal connection");
        }
    }, [state.connected]);
    const resize = react_1.useCallback(async (height) => {
        if (!penpalConnectionRef.current) {
            throw new Error("No penpal connection");
        }
        const parent = await penpalConnectionRef.current.promise;
        await parent.resize(height);
    }, []);
    const saveIntegrationConfig = react_1.useCallback(async (config, options) => {
        assertConnected();
        const parent = await penpalConnectionRef.current.promise;
        const data = await parent.saveIntegration(config, options);
        setState((state) => ({
            ...state,
            integrationConfig: data.upsertIntegration.config,
        }));
    }, [assertConnected]);
    const patchIntegrationConfig = react_1.useCallback(async (patch, options) => {
        assertConnected();
        const parent = await penpalConnectionRef.current.promise;
        const data = await parent.patchIntegrationConfig(patch, options);
        setState((state) => ({
            ...state,
            integrationConfig: data.patchIntegrationConfig.config,
        }));
    }, [assertConnected]);
    const saveFormConfig = react_1.useCallback(async (config, options) => {
        assertConnected();
        const parent = await penpalConnectionRef.current.promise;
        await parent.updateFormConfiguration(config, options);
        setState((state) => ({ ...state, formConfig: config }));
    }, [assertConnected]);
    const getFileStackConfig = react_1.useCallback(async () => {
        assertConnected();
        const parent = await penpalConnectionRef.current.promise;
        return parent.getFileStackConfig();
    }, [assertConnected]);
    const navigatePortal = react_1.useCallback(async (url) => {
        assertConnected();
        const parent = await penpalConnectionRef.current.promise;
        await parent.navigateToNewPortalURL(url);
    }, [assertConnected]);
    const closeFormConfig = react_1.useCallback(async () => {
        assertConnected();
        saveFormConfig(state
            .formConfig);
    }, [assertConnected, saveFormConfig, state]);
    const scrollTo = react_1.useCallback(async (scrollX, scrollY) => {
        assertConnected();
        const parent = await penpalConnectionRef.current.promise;
        await parent.scrollTo(scrollX, scrollY);
    }, [assertConnected]);
    const disableIntegration = react_1.useCallback(async () => {
        assertConnected();
        const parent = await penpalConnectionRef.current.promise;
        await parent.disableIntegration();
    }, [assertConnected]);
    react_1.useEffect(() => {
        (async () => {
            penpalConnectionRef.current = penpal_1.connectToParent({
                timeout: 5000,
                methods: {
                    displayConfiguration({ tenantScopedToken, tenantAlias, integrationConfig, formConfig, formType, flavor, }) {
                        let mode = ConfigMode.Unknown;
                        if (formType) {
                            switch (formType) {
                                case "submit_actions":
                                    mode = ConfigMode.FormConfigSubmitActions;
                                    break;
                                case "initial_data_actions":
                                    mode = ConfigMode.FormConfigInitialDataActions;
                                    break;
                                default:
                                    throw new Error(`Unknown form configuration mode: ${formType}`);
                            }
                        }
                        else {
                            mode = ConfigMode.IntegrationsPage;
                        }
                        setState({
                            connected: true,
                            mode,
                            tenantScopedToken,
                            tenantAlias,
                            integrationConfig,
                            formConfig: formConfig || {},
                            flavor,
                        });
                    },
                    async shouldCancelDisable() {
                        if (shouldCancelDisableCallbackRef.current) {
                            return await shouldCancelDisableCallbackRef.current();
                        }
                        return false;
                    },
                },
            });
            try {
                await penpalConnectionRef.current.promise;
            }
            catch (e) {
                setTriedToConnect(true);
                return;
            }
            resizeObserverRef.current = new resize_observer_polyfill_1.default((entries) => {
                for (const entry of entries) {
                    const { height } = entry.contentRect;
                    resize(height);
                }
            });
            resizeObserverRef.current.observe(document.body);
        })();
        return () => {
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
                resizeObserverRef.current = null;
            }
        };
    }, [resize]);
    const value = react_1.useMemo(() => ({
        ...state,
        resize,
        saveIntegrationConfig,
        patchIntegrationConfig,
        saveFormConfig,
        getFileStackConfig,
        navigatePortal,
        scrollTo,
        disableIntegration,
        closeFormConfig,
        setShouldCancelDisableCallback,
    }), [
        state,
        resize,
        saveIntegrationConfig,
        patchIntegrationConfig,
        saveFormConfig,
        getFileStackConfig,
        navigatePortal,
        scrollTo,
        disableIntegration,
        closeFormConfig,
        setShouldCancelDisableCallback,
    ]);
    return state.connected ? (react_1.default.createElement(exports.PenpalContext.Provider, { value: value }, props.children)) : triedToConnect && props.fallback ? (props.fallback) : (props.loading || null);
}
exports.PenpalContextProvider = PenpalContextProvider;
function usePenpal() {
    const penpal = react_1.useContext(exports.PenpalContext);
    if (!penpal.connected) {
        throw new Error("Not wrapped in PenpalContextProvider or no Penpal connection");
    }
    return penpal;
}
exports.default = usePenpal;
